import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus } from "lucide-react"
import { useRef, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { normalizarDecimal } from "@/componentes/ordenes/lineas"
import { monto } from "@/componentes/tesoreria/dinero"
import type { Divisa } from "@/componentes/tesoreria/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { Modal } from "@/componentes/ui/Modal"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { Campo } from "@/paginas/interno/Gastos"

type Asume = "EMISOR" | "RECEPTOR" | "NINGUNO"

type MetodoPago = {
  id: number
  codigo: string
  nombre: string
  descripcion: string | null
  divisa_id: number | null
  comision_pct: string
  comision_fija: string
  comision_minima: string
  comision_maxima: string | null
  asume: Asume
  iva_sobre_comision: boolean
  /** Si un pago por esta vía lleva IGTF en una cuenta en divisas (21/10; P2P no). */
  aplica_igtf: boolean
  activo: boolean
}

/** Quién carga con la comisión, en las palabras de tesorería. */
const ASUME: Record<Asume, string> = {
  EMISOR: "La paga la agencia",
  RECEPTOR: "La asume quien cobra",
  NINGUNO: "Sin comisión",
}

/**
 * Cómo sale el dinero, y cuánto cobra el banco por ello.
 *
 * ⚠ **Las tarifas viven aquí y no en el código.** Los métodos nacen con las cifras
 * vigentes en Venezuela —pago móvil 0,3 % con un mínimo de 14 Bs, wire 35 $— pero
 * cambian por resolución y por convenio: esta pantalla es la que manda, y lo que se
 * ajuste aquí sobrevive al siguiente despliegue porque la siembra no pisa una fila
 * existente.
 *
 * ⚠ **La comisión calculada es una SUGERENCIA para el pago**, no la cifra que se
 * asienta: al ejecutar se teclea la que el banco debitó de verdad.
 *
 * ⚠ **La moneda hace dos cosas.** Dice en qué moneda están el fijo, el mínimo y el
 * máximo, y limita el método a las cuentas de esa moneda: un pago móvil no sale de
 * una cuenta en dólares, y sus «14» no son catorce dólares. Vacía es «cualquier
 * cuenta», que es el caso del efectivo.
 *
 * ⚠ **Quién asume la comisión decide si se sugiere o no.** Si la carga quien cobra
 * —el punto de venta se la descuenta al comercio— de esta cuenta no sale nada, y
 * sugerir un importe sería apuntar un gasto que no existe.
 */
export function MetodosPago() {
  const usuario = usarSesion((e) => e.usuario)
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [enEdicion, setEnEdicion] = useState<MetodoPago | null>(null)
  const puedeAdministrar = tienePermiso(usuario, "tesoreria:administrar")
  const vista = useRef<HTMLElement | null>(null)

  const metodos = useQuery({
    queryKey: ["metodos-pago"],
    queryFn: () => api.get<MetodoPago[]>("/api/v1/metodos-pago"),
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
  })

  /** El código de la moneda, que es lo que se lee; el id no dice nada. */
  function codigoDivisa(id: number | null): string | null {
    if (id === null) return null
    return (divisas.data ?? []).find((d) => d.id === id)?.codigo ?? null
  }

  const guardar = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      enEdicion
        ? api.patch(`/api/v1/metodos-pago/${enEdicion.id}`, cuerpo)
        : api.post("/api/v1/metodos-pago", cuerpo),
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["metodos-pago"] })
      setAbierto(false)
    },
  })

  function abrir(metodo: MetodoPago | null) {
    guardar.reset()
    setEnEdicion(metodo)
    setAbierto(true)
  }

  function enviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const datos = new FormData(evento.currentTarget)
    const numero = (campo: string, vacioEs: string | null) => {
      const texto = String(datos.get(campo) ?? "").trim()
      return texto === "" ? vacioEs : (normalizarDecimal(texto) ?? texto)
    }
    const descripcion = String(datos.get("descripcion") ?? "").trim()
    const divisa = String(datos.get("divisa_id") ?? "")
    const cuerpo: Record<string, unknown> = {
      nombre: String(datos.get("nombre") ?? "").trim(),
      descripcion: descripcion === "" ? null : descripcion,
      divisa_id: divisa === "" ? null : Number(divisa),
      comision_pct: numero("comision_pct", "0"),
      comision_fija: numero("comision_fija", "0"),
      comision_minima: numero("comision_minima", "0"),
      comision_maxima: numero("comision_maxima", null),
      asume: String(datos.get("asume") ?? "EMISOR"),
      iva_sobre_comision: datos.get("iva_sobre_comision") === "on",
      aplica_igtf: datos.get("aplica_igtf") === "on",
    }
    if (enEdicion) cuerpo.activo = datos.get("activo") === "on"
    guardar.mutate(cuerpo)
  }

  if (metodos.isPending) return <p className="py-6">Cargando…</p>
  if (metodos.isError) {
    return (
      <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
        <AlertCircle size={16} /> No se pudieron cargar los métodos de pago.
      </p>
    )
  }

  const error = guardar.error instanceof ErrorApi ? guardar.error.message : null

  return (
    <section ref={vista} className="space-y-4">
      <div className="flex flex-wrap items-start justify-end gap-2">
        {metodos.data.length > 0 && (
          <Exportar
            titulo="Métodos de pago"
            subtitulo="Comisiones sugeridas de cada método; los importes, en la moneda de la cuenta que paga"
            contenido={vista}
          />
        )}
        {puedeAdministrar && (
          <Boton variante="primario" onClick={() => abrir(null)}>
            <Plus size={16} /> Nuevo método
          </Boton>
        )}
      </div>
      <Tabla etiqueta="Métodos de pago">
        <Cabecera>
          <Th>Método</Th>
          <Th>Moneda</Th>
          <Th derecha>% comisión</Th>
          <Th derecha>Fija</Th>
          <Th derecha>Mínima</Th>
          <Th derecha>Máxima</Th>
          <Th>Comisión</Th>
          <Th>Estado</Th>
        </Cabecera>
        <Cuerpo>
          {metodos.data.length === 0 && (
            <TablaVacia colSpan={8}>No hay métodos de pago.</TablaVacia>
          )}
          {metodos.data.map((m) => (
            <Fila
              key={m.id}
              atenuada={!m.activo}
              className={puedeAdministrar ? "cursor-pointer" : undefined}
              onClick={puedeAdministrar ? () => abrir(m) : undefined}
            >
              <Td>
                {m.nombre}
                {m.descripcion && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {m.descripcion}
                  </span>
                )}
              </Td>
              <Td>{codigoDivisa(m.divisa_id) ?? "Cualquiera"}</Td>
              <Td derecha numero>
                {Number(m.comision_pct).toLocaleString("es-VE")} %
              </Td>
              <Td derecha numero data-exportar-valor={monto(m.comision_fija)}>
                {m.comision_fija}
              </Td>
              <Td derecha numero data-exportar-valor={monto(m.comision_minima)}>
                {m.comision_minima}
              </Td>
              {/* Sin tope sale vacío en el Excel: así la columna sigue siendo de cifras. */}
              <Td
                derecha
                numero
                data-exportar-valor={m.comision_maxima === null ? "" : monto(m.comision_maxima)}
              >
                {m.comision_maxima ?? "sin tope"}
              </Td>
              <Td>
                {ASUME[m.asume]}
                {m.iva_sobre_comision && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    con IVA
                  </span>
                )}
                {!m.aplica_igtf && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    sin IGTF
                  </span>
                )}
              </Td>
              <Td>
                <Insignia tono={m.activo ? "exito" : "neutro"}>
                  {m.activo ? "Activo" : "Archivado"}
                </Insignia>
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>

      <Modal
        abierto={abierto}
        titulo={enEdicion ? `Editar ${enEdicion.nombre}` : "Nuevo método de pago"}
        descripcion="La comisión es una sugerencia: al pagar se teclea la que cobró el banco."
        ancho="sm"
        onCerrar={() => setAbierto(false)}
      >
        <form key={enEdicion?.id ?? "nuevo"} className="space-y-4" onSubmit={enviar}>
          <Campo para="metodo-nombre" etiqueta="Nombre">
            <input
              id="metodo-nombre"
              name="nombre"
              required
              minLength={2}
              maxLength={80}
              defaultValue={enEdicion?.nombre ?? ""}
              className="campo mt-1"
            />
          </Campo>
          <Campo para="metodo-descripcion" etiqueta="Qué cobra" ayuda="Se lee al pagar.">
            <input
              id="metodo-descripcion"
              name="descripcion"
              maxLength={255}
              defaultValue={enEdicion?.descripcion ?? ""}
              className="campo mt-1"
            />
          </Campo>
          <Campo
            para="metodo-divisa"
            etiqueta="Moneda de la tarifa"
            ayuda="Y única moneda de cuenta que admite el método. Vacía: cualquiera."
          >
            <select
              id="metodo-divisa"
              name="divisa_id"
              defaultValue={enEdicion?.divisa_id ?? ""}
              className="campo mt-1"
            >
              <option value="">Cualquiera</option>
              {(divisas.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.codigo}
                </option>
              ))}
            </select>
          </Campo>
          <Campo para="metodo-asume" etiqueta="Quién asume la comisión">
            <select
              id="metodo-asume"
              name="asume"
              defaultValue={enEdicion?.asume ?? "EMISOR"}
              className="campo mt-1"
            >
              {(Object.keys(ASUME) as Asume[]).map((clave) => (
                <option key={clave} value={clave}>
                  {ASUME[clave]}
                </option>
              ))}
            </select>
          </Campo>
          <div className="grid grid-cols-2 gap-4">
            <Campo para="metodo-pct" etiqueta="% comisión">
              <input
                id="metodo-pct"
                name="comision_pct"
                inputMode="decimal"
                defaultValue={enEdicion ? String(Number(enEdicion.comision_pct)) : "0"}
                className="campo mt-1 tabular-nums"
              />
            </Campo>
            <Campo para="metodo-fija" etiqueta="Comisión fija">
              <input
                id="metodo-fija"
                name="comision_fija"
                inputMode="decimal"
                defaultValue={enEdicion?.comision_fija ?? "0"}
                className="campo mt-1 tabular-nums"
              />
            </Campo>
            <Campo para="metodo-min" etiqueta="Mínima">
              <input
                id="metodo-min"
                name="comision_minima"
                inputMode="decimal"
                defaultValue={enEdicion?.comision_minima ?? "0"}
                className="campo mt-1 tabular-nums"
              />
            </Campo>
            <Campo para="metodo-max" etiqueta="Máxima" ayuda="Vacía: sin tope.">
              <input
                id="metodo-max"
                name="comision_maxima"
                inputMode="decimal"
                defaultValue={enEdicion?.comision_maxima ?? ""}
                className="campo mt-1 tabular-nums"
              />
            </Campo>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="iva_sobre_comision"
              defaultChecked={enEdicion?.iva_sobre_comision ?? false}
            />
            El banco cobra IVA sobre la comisión
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="aplica_igtf"
              defaultChecked={enEdicion?.aplica_igtf ?? true}
            />
            Lleva IGTF en cuentas en divisas (P2P de Binance, no)
          </label>
          {enEdicion && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" name="activo" defaultChecked={enEdicion.activo} />
              Activo
            </label>
          )}
          {error && (
            <p className="text-sm" style={{ color: "var(--destructive)" }}>
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Boton onClick={() => setAbierto(false)}>Cancelar</Boton>
            <Boton type="submit" variante="primario" disabled={guardar.isPending}>
              {guardar.isPending ? "Guardando…" : "Guardar"}
            </Boton>
          </div>
        </form>
      </Modal>
    </section>
  )
}
